import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { ProductListRow } from "@redotech/redo-web/product-list-row";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";

export const ReshipModal = memo(function ReshipModal({
  isOpen,
  onCancel,
  onSuccess,
  isOnIms,
  items,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (selectedLineItemExternalIds: string[]) => void;
  isOnIms: boolean;
  items: FulfillmentOrderLineItem[];
}) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const visibleItems = useMemo(
    () => items.filter((item) => item.quantity > 0),
    [items],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedItems(new Set(visibleItems.map((item) => item.externalId)));
    }
  }, [isOpen, visibleItems]);

  const selectedCount = selectedItems.size;
  const totalCount = visibleItems.length;

  const selectAllValue: CheckboxValue = useMemo(() => {
    if (selectedCount === 0) {
      return false;
    }
    if (selectedCount === totalCount) {
      return true;
    }
    return "indeterminate";
  }, [selectedCount, totalCount]);

  const toggleSelectAll = useHandler(() => {
    if (selectAllValue === true) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(visibleItems.map((item) => item.externalId)));
    }
  });

  const toggleItem = useHandler((externalId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(externalId)) {
        next.delete(externalId);
      } else {
        next.add(externalId);
      }
      return next;
    });
  });

  const handleConfirm = useHandler(() => {
    onSuccess(Array.from(selectedItems));
  });

  const message = isOnIms
    ? "This action will not update the order status in the marketplace. It will update inventory quantities in the marketplace"
    : "This action will not update the order status, or your item quantities in the marketplace.";

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="md"
      onModalCloseRequested={onCancel}
      primaryButton={{
        text: "Confirm",
        onClick: async () => {
          handleConfirm();
        },
        disabled: selectedCount === 0,
      }}
      secondaryButton={{ text: "Cancel", onClick: onCancel }}
      subtitle="Select items to include in the reshipped shipment."
      title="Duplicate shipment"
    >
      <Flex dir="column" gap="xl">
        <Flex align="center" w="full">
          <RedoCheckbox
            label={`Select all (${selectedCount}/${totalCount})`}
            setValue={toggleSelectAll}
            size="sm"
            value={selectAllValue}
          />
        </Flex>
        <Flex
          borderColor="primary"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="none"
          radius="sm"
        >
          {visibleItems.map((item, index) => (
            <Flex
              align="center"
              borderBottomWidth={
                index !== visibleItems.length - 1 ? "1px" : "none"
              }
              borderColor="secondary"
              borderStyle="solid"
              gap="md"
              key={item.externalId}
              pl="lg"
              w="full"
            >
              <RedoCheckbox
                setValue={() => toggleItem(item.externalId)}
                size="sm"
                value={selectedItems.has(item.externalId)}
              />
              <ProductListRow
                imageUrl={item.imageUrl}
                price={item.unitDiscountedPrice.amount}
                priceMoney={toMoney(item.unitDiscountedPrice)}
                quantity={item.quantity}
                sku={item.sku}
                title={item.productTitle || item.title}
                variantTitle={item.variantTitle}
              />
            </Flex>
          ))}
        </Flex>
        <Text fontSize="md" fontWeight="thin" textColor="primary">
          This action will duplicate this shipment, and set the duplicate
          shipment's status to awaiting shipment, and add it to the existing
          order.
        </Text>
        <Text fontSize="md" fontWeight="thin" textColor="primary">
          {message}
        </Text>
      </Flex>
    </RedoModal>
  );
});
